import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const CategoryManagement = () => {
    const [blogCategories, setBlogCategories] = useState([]);
    const [portfolioCategories, setPortfolioCategories] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [currentType, setCurrentType] = useState('blog');
    const { toast } = useToast();
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
        type: 'blog',
        color: '#3b82f6',
        order: 0,
        isActive: true,
    });
    useEffect(() => {
        fetchCategories();
    }, []);
    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/categories/all`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setBlogCategories(data.filter((c) => c.type === 'blog'));
                setPortfolioCategories(data.filter((c) => c.type === 'portfolio'));
            }
        }
        catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };
    const generateSlug = (name) => {
        return name
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };
    const handleNameChange = (name) => {
        setFormData({
            ...formData,
            name,
            slug: editingCategory ? formData.slug : generateSlug(name),
        });
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const url = editingCategory
                ? `${API_URL}/categories/${editingCategory.id}`
                : `${API_URL}/categories`;
            const response = await fetch(url, {
                method: editingCategory ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });
            if (response.ok) {
                toast({
                    title: editingCategory ? 'Category Updated' : 'Category Created',
                    description: `${formData.name} has been ${editingCategory ? 'updated' : 'created'} successfully.`,
                });
                setIsOpen(false);
                resetForm();
                fetchCategories();
            }
            else {
                const error = await response.json();
                toast({
                    title: 'Error',
                    description: error.error || 'Failed to save category',
                    variant: 'destructive',
                });
            }
        }
        catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to save category',
                variant: 'destructive',
            });
        }
    };
    const handleDelete = async (id, name) => {
        if (!confirm(`Are you sure you want to delete "${name}"?`))
            return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/categories/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                toast({
                    title: 'Category Deleted',
                    description: `${name} has been deleted successfully.`,
                });
                fetchCategories();
            }
        }
        catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to delete category',
                variant: 'destructive',
            });
        }
    };
    const handleEdit = (category) => {
        setEditingCategory(category);
        setFormData({
            name: category.name,
            slug: category.slug,
            description: category.description || '',
            type: category.type,
            color: category.color || '#3b82f6',
            order: category.order,
            isActive: category.isActive,
        });
        setIsOpen(true);
    };
    const resetForm = () => {
        setEditingCategory(null);
        setFormData({
            name: '',
            slug: '',
            description: '',
            type: currentType,
            color: '#3b82f6',
            order: 0,
            isActive: true,
        });
    };
    const handleAddNew = (type) => {
        setCurrentType(type);
        resetForm();
        setFormData(prev => ({ ...prev, type }));
        setIsOpen(true);
    };
    const CategoryCard = ({ category }) => {
        const count = category.type === 'blog'
            ? category._count?.blogPosts || 0
            : category._count?.portfolioItems || 0;
        return (_jsxs(Card, { children: [_jsxs(CardHeader, { className: "flex flex-row items-center justify-between space-y-0 pb-2", children: [_jsxs("div", { className: "flex items-center gap-2", children: [category.color && (_jsx("div", { className: "w-4 h-4 rounded-full border", style: { backgroundColor: category.color } })), _jsx(CardTitle, { className: "text-lg font-semibold", children: category.name })] }), _jsx("div", { className: "flex gap-2", children: !category.isActive && _jsx(Badge, { variant: "secondary", children: "Inactive" }) })] }), _jsx(CardContent, { children: _jsxs("div", { className: "space-y-2", children: [_jsxs("p", { className: "text-sm text-muted-foreground", children: ["Slug: ", _jsx("code", { className: "bg-muted px-1 rounded", children: category.slug })] }), category.description && (_jsx("p", { className: "text-sm text-muted-foreground", children: category.description })), _jsxs("p", { className: "text-sm text-muted-foreground", children: [count, " ", category.type === 'blog' ? 'posts' : 'items'] }), _jsxs("div", { className: "flex gap-2 pt-2", children: [_jsxs(Button, { size: "sm", variant: "outline", onClick: () => handleEdit(category), children: [_jsx(Pencil, { className: "h-4 w-4 mr-1" }), "Edit"] }), _jsxs(Button, { size: "sm", variant: "destructive", onClick: () => handleDelete(category.id, category.name), children: [_jsx(Trash2, { className: "h-4 w-4 mr-1" }), "Delete"] })] })] }) })] }));
    };
    return (_jsxs("div", { className: "space-y-6", children: [_jsx("div", { className: "flex justify-between items-center", children: _jsxs("div", { children: [_jsx("h2", { className: "text-3xl font-bold tracking-tight", children: "Category Management" }), _jsx("p", { className: "text-muted-foreground", children: "Manage categories for blog posts and portfolio items" })] }) }), _jsxs(Tabs, { defaultValue: "blog", className: "space-y-4", children: [_jsxs(TabsList, { children: [_jsx(TabsTrigger, { value: "blog", children: "Blog Categories" }), _jsx(TabsTrigger, { value: "portfolio", children: "Portfolio Categories" })] }), _jsxs(TabsContent, { value: "blog", className: "space-y-4", children: [_jsx("div", { className: "flex justify-end", children: _jsxs(Button, { onClick: () => handleAddNew('blog'), children: [_jsx(Plus, { className: "mr-2 h-4 w-4" }), "Add Blog Category"] }) }), _jsxs("div", { className: "grid gap-4 md:grid-cols-2 lg:grid-cols-3", children: [blogCategories.map((category) => (_jsx(CategoryCard, { category: category }, category.id))), blogCategories.length === 0 && (_jsx("div", { className: "col-span-full text-center py-12 text-muted-foreground", children: "No blog categories yet. Create your first one!" }))] })] }), _jsxs(TabsContent, { value: "portfolio", className: "space-y-4", children: [_jsx("div", { className: "flex justify-end", children: _jsxs(Button, { onClick: () => handleAddNew('portfolio'), children: [_jsx(Plus, { className: "mr-2 h-4 w-4" }), "Add Portfolio Category"] }) }), _jsxs("div", { className: "grid gap-4 md:grid-cols-2 lg:grid-cols-3", children: [portfolioCategories.map((category) => (_jsx(CategoryCard, { category: category }, category.id))), portfolioCategories.length === 0 && (_jsx("div", { className: "col-span-full text-center py-12 text-muted-foreground", children: "No portfolio categories yet. Create your first one!" }))] })] })] }), _jsx(Dialog, { open: isOpen, onOpenChange: setIsOpen, children: _jsxs(DialogContent, { className: "max-w-2xl", children: [_jsx(DialogHeader, { children: _jsx(DialogTitle, { children: editingCategory ? 'Edit Category' : 'Add New Category' }) }), _jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "name", children: "Name *" }), _jsx(Input, { id: "name", value: formData.name, onChange: (e) => handleNameChange(e.target.value), required: true })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "slug", children: "Slug *" }), _jsx(Input, { id: "slug", value: formData.slug, onChange: (e) => setFormData({ ...formData, slug: e.target.value }), required: true })] })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "description", children: "Description" }), _jsx(Textarea, { id: "description", value: formData.description, onChange: (e) => setFormData({ ...formData, description: e.target.value }), rows: 3 })] }), _jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "color", children: "Color" }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Input, { id: "color", type: "color", value: formData.color, onChange: (e) => setFormData({ ...formData, color: e.target.value }), className: "w-20 h-10" }), _jsx(Input, { type: "text", value: formData.color, onChange: (e) => setFormData({ ...formData, color: e.target.value }), placeholder: "#3b82f6" })] })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "order", children: "Order" }), _jsx(Input, { id: "order", type: "number", value: formData.order, onChange: (e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 }) })] })] }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Switch, { id: "active", checked: formData.isActive, onCheckedChange: (checked) => setFormData({ ...formData, isActive: checked }) }), _jsx(Label, { htmlFor: "active", children: "Active" })] }), _jsxs("div", { className: "flex justify-end gap-2", children: [_jsx(Button, { type: "button", variant: "outline", onClick: () => setIsOpen(false), children: "Cancel" }), _jsx(Button, { type: "submit", children: editingCategory ? 'Update' : 'Create' })] })] })] }) })] }));
};
export default CategoryManagement;
